import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import { Skeleton } from "../components/ui/Skeleton";
import { useLanguage } from "../context/LanguageContext";

const RegisterPage: React.FC = () => {
  const { register, isAuthenticated, isInitializing } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => { document.title = "Create Account | Acebarter"; }, []);

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, isInitializing, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setError("");

    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (!agreed) { setError("Please agree to the Terms of Service"); return; }

    setIsLoading(true);
    try {
      await register(email.trim(), password, name);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || "Registration failed. Please try again.");
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-5">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left — Brand panel (desktop only) */}
      <div className="hidden lg:flex w-[420px] bg-accent/5 border-r border-border flex-col items-center justify-center p-12">
        <img src="/favicon.png" alt="Acebarter" width="64" height="64" className="w-16 h-16 mb-6" />
        <h2 className="text-xl font-bold text-foreground text-center mb-3">
          Start Your Crypto Journey
        </h2>
        <p className="text-sm text-muted-foreground text-center max-w-xs mb-8">
          Join thousands of traders and investors on Acebarter. Create your free account in seconds.
        </p>
        <div className="space-y-3 w-full max-w-xs">
          {[
            { icon: "Zap", text: "Instant account setup — no delays" },
            { icon: "Wallet", text: "Deposit & withdraw multiple cryptos" },
            { icon: "GraduationCap", text: "Free crypto academy & learning tools" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3 bg-card/50 border border-border rounded-lg px-4 py-3">
              <Icon name={item.icon} size={16} className="text-accent flex-shrink-0" />
              <span className="text-xs text-foreground">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="h-16 px-6 flex items-center justify-between flex-shrink-0">
          <button onClick={() => navigate("/")} className="hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="Acebarter" width="100" height="28" className="h-7" />
          </button>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-muted-foreground hidden sm:inline">{t('auth.hasAccount')}</span>
            <button onClick={() => navigate("/login")} className="text-accent font-semibold hover:underline">
              {t('auth.login')}
            </button>
          </div>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-6 pb-12">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-bold text-foreground mb-1">{t('auth.register')}</h1>
            <p className="text-sm text-muted-foreground mb-8">{t('auth.getStarted')}</p>

            {error && (
              <div className="bg-error-muted border border-error/20 rounded-lg px-4 py-3 mb-5 flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('auth.name')}</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)} required disabled={isLoading}
                  className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all disabled:opacity-50"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('auth.email')}</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading}
                  className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all disabled:opacity-50"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('auth.password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)} required disabled={isLoading}
                    className="w-full px-4 py-3 pr-11 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all disabled:opacity-50"
                    placeholder={t('auth.minChars')}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                    <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('auth.confirmPassword')}</label>
                <input
                  type={showPassword ? "text" : "password"} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required disabled={isLoading}
                  className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground/40 focus:ring-2 focus:ring-accent/30 focus:border-accent/30 outline-none transition-all disabled:opacity-50"
                  placeholder="Re-enter password"
                />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 rounded border-border accent-accent"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{" "}
                  <button type="button" onClick={() => navigate("/terms")} className="text-accent hover:underline">Terms of Service</button>
                  {" "}and{" "}
                  <button type="button" onClick={() => navigate("/privacy")} className="text-accent hover:underline">Privacy Policy</button>
                </span>
              </label>

              <button type="submit" disabled={isLoading}
                className="w-full bg-accent hover:bg-accent-dark text-accent-foreground py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" /> Creating account...</>
                ) : t('auth.register')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
